/**
 * Document Dashboard Tab Component
 * 
 * Generic, reusable dashboard component for document detail pages.
 * Fetches dashboard data from backend and renders sections (tables, charts).
 * 
 * Features:
 * - Generic and doctype-agnostic
 * - Lazy loading (only fetches when tab is active)
 * - React Query caching
 * - Graceful error handling
 * - Empty state handling
 */

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getDocDashboard } from "@/lib/erp/docs";
import { getQueryOptions } from "@/lib/config/queryConfig";
import { DashboardTableSection } from "./DashboardTableSection";
import { DashboardChartSection } from "./DashboardChartSection";
import { DashboardCardSection } from "./DashboardCardSection";
import { useDetailPageTabs } from "@/contexts/DetailPageTabsContext";
import { DASHBOARD_TAB_ID } from "./useDashboardTab";
import type { DashboardSection } from "@/lib/erp/docs";
import type { ViewStyleKey } from "@/lib/views/types";
import { useLanguage } from "@/i18n/useLanguage";
import { getUITranslation } from "@/lib/views/labels/translations";

interface DocDashboardTabProps {
  doctype: string;
  name: string;
  viewMode?: 'system' | 'experience';
  viewStyle?: ViewStyleKey | 'default' | undefined;
}

export function DocDashboardTab({ 
  doctype, 
  name, 
  viewMode = 'system',
  viewStyle = undefined 
}: DocDashboardTabProps) {
  const { activeTab } = useDetailPageTabs();
  const { language } = useLanguage();
  const ui = useMemo(() => {
    const t = (key: string, fallback: string) => getUITranslation(key, language) || fallback;
    return {
      dashboardFailedToLoad: t(
        "dashboard_failed_to_load",
        "Failed to load dashboard data. Please try refreshing the page."
      ),
      dashboardNoDataConfigured: t(
        "dashboard_no_data_configured",
        "No dashboard data configured for this document yet."
      ),
    };
  }, [language]);
  
  // Only fetch when Dashboard tab is active (lazy loading)
  const isActive = activeTab === DASHBOARD_TAB_ID;
  
  // Determine if we should use card rendering for stock levels
  const isShopifyExperience = viewMode === 'experience' && viewStyle === 'shopify';
  
  const { data, isLoading, error } = useQuery({
    queryKey: ['doc-dashboard', doctype, name],
    queryFn: () => getDocDashboard(doctype, name),
    enabled: isActive && !!doctype && !!name && name !== 'new',
    ...getQueryOptions("detail"),
  });
  
  // Sort sections: stock_summary (chart) should appear before stock_levels (table/cards)
  // IMPORTANT: This hook must be called before any early returns to follow Rules of Hooks
  const sortedSections = useMemo(() => {
    if (!data?.sections) return [];
    
    const sections = [...data.sections];
    
    // Find indices
    const stockSummaryIndex = sections.findIndex(
      s => s.id === 'stock_summary' || (s.type === 'chart' && s.meta?.chart_type === 'stock_balance')
    );
    const stockLevelsIndex = sections.findIndex(
      s => s.id === 'stock_levels' && s.type === 'table'
    );
    
    // If both exist and stock_summary comes after stock_levels, reorder
    if (stockSummaryIndex !== -1 && stockLevelsIndex !== -1 && stockSummaryIndex > stockLevelsIndex) {
      const [stockSummary] = sections.splice(stockSummaryIndex, 1);
      sections.splice(stockLevelsIndex, 0, stockSummary);
    }
    
    return sections;
  }, [data?.sections]);
  
  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-8">
        {[1, 2].map((i) => (
          <div
            key={i}
            className="rounded-2xl border-0 bg-white dark:bg-gray-900 shadow-sm p-8 sm:p-10 animate-pulse"
          >
            <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded-md w-1/4 mb-4" />
            <div className="h-32 bg-gray-200 dark:bg-gray-800 rounded-md" />
          </div>
        ))}
      </div>
    );
  }
  
  // Error state (inline, don't crash tab)
  if (error) {
    return (
      <div>
        <div className="rounded-2xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/20 p-4">
          <p className="text-sm text-red-600 dark:text-red-400">
            {ui.dashboardFailedToLoad}
          </p>
        </div>
      </div>
    );
  }
  
  // Empty state
  if (!data || !data.sections || data.sections.length === 0) {
    return (
      <div>
        <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950/20 p-12 text-center">
          <p className="text-sm text-muted-foreground">
            {ui.dashboardNoDataConfigured}
          </p>
        </div>
      </div>
    );
  }
  
  // Debug logging (dev only)
  if (import.meta.env.DEV && data) {
    console.log('[DocDashboardTab] Dashboard data received:', {
      doctype: data.doctype,
      name: data.name,
      sectionsCount: data.sections?.length,
      sections: data.sections?.map(s => ({
        id: s.id,
        type: s.type,
        chart_type: s.meta?.chart_type,
      })),
    });
  }
  
  // Get warehouse count from stock_summary section for empty state handling
  const stockSummarySection = data?.sections?.find(
    s => s.id === 'stock_summary' || (s.type === 'chart' && s.meta?.chart_type === 'stock_balance')
  );
  const warehouseCount = stockSummarySection?.meta?.warehouse_count ?? 0;
  const itemCode = doctype === 'Item' ? name : undefined;
  
  // Render section based on view context
  function renderSection(section: DashboardSection) {
    // 1) Shopify Experience + Item + stock_levels → CARDS
    if (
      isShopifyExperience &&
      doctype === 'Item' &&
      section.id === 'stock_levels' &&
      section.type === 'table'
    ) {
      return (
        <DashboardCardSection
          key={section.id}
          label={section.label || ''}
          columns={section.columns ?? []}
          rows={section.rows ?? []}
          itemCode={itemCode}
          warehouseCount={warehouseCount}
        />
      );
    }
    
    // 2) Stock summary → MINIMAL GAUGE (already handled by DashboardChartSection with chart_type: "stock_balance")
    if (section.type === 'chart') {
      return (
        <DashboardChartSection
          key={section.id}
          section={section}
          itemCode={itemCode}
        />
      );
    }
    
    // 3) Default table
    if (section.type === 'table') {
      return (
        <DashboardTableSection
          key={section.id}
          section={section}
          itemCode={itemCode}
          warehouseCount={warehouseCount}
        />
      );
    }
    
    // Skip unknown section types (log warning in dev)
    if (import.meta.env.DEV) {
      console.warn(
        `[DocDashboardTab] Unknown section type "${section.type}" for section "${section.id}". Skipping.`
      );
    }
    return null;
  }
  
  // Render sections - match details page spacing (space-y-8 like TabRenderer)
  return (
    <div className="space-y-8">
      {sortedSections.map((section: DashboardSection) => renderSection(section))}
    </div>
  );
}
